import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

type Props = {
  latitude?: number
  longitude?: number
  radiusMeters: number
  label?: string
  readOnly?: boolean
  onChange: (point: { latitude: number; longitude: number }) => void
  onMapUnavailable?: () => void
}

const DEFAULT_CENTER: [number, number] = [45.5017, -73.5673]
const OSM_MAX_ZOOM = 19
const pinIcon = L.divIcon({
  className: 'setup-leaflet-pin',
  html: '<span></span>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

const isValidLatitude = (value: unknown) => Number.isFinite(Number(value)) && Number(value) >= -90 && Number(value) <= 90
const isValidLongitude = (value: unknown) => Number.isFinite(Number(value)) && Number(value) >= -180 && Number(value) <= 180
const formatCoordinate = (value: number) => Number(value.toFixed(6))

export const LeafletGeofencePicker: React.FC<Props> = ({
  latitude,
  longitude,
  radiusMeters,
  label = 'Selected location',
  readOnly = false,
  onChange,
  onMapUnavailable,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const circleRef = useRef<L.Circle | null>(null)
  const callbacksRef = useRef({ onChange })
  const [mapReady, setMapReady] = useState(false)
  const hasPoint = isValidLatitude(latitude) && isValidLongitude(longitude)

  useEffect(() => {
    callbacksRef.current = { onChange }
  }, [onChange])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    try {
      const map = L.map(containerRef.current, { zoomControl: true, maxZoom: OSM_MAX_ZOOM }).setView(
        hasPoint ? [Number(latitude), Number(longitude)] : DEFAULT_CENTER,
        hasPoint ? 18 : 12
      )
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: OSM_MAX_ZOOM,
        maxNativeZoom: OSM_MAX_ZOOM,
      }).addTo(map)

      map.on('click', (event: L.LeafletMouseEvent) => {
        if (readOnly) return
        callbacksRef.current.onChange({
          latitude: formatCoordinate(event.latlng.lat),
          longitude: formatCoordinate(event.latlng.lng),
        })
      })

      mapRef.current = map
      setMapReady(true)
      window.setTimeout(() => map.invalidateSize(), 0)
    } catch {
      onMapUnavailable?.()
    }

    return () => {
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !hasPoint) return
    const center: [number, number] = [Number(latitude), Number(longitude)]

    if (!circleRef.current) {
      circleRef.current = L.circle(center, {
        radius: Math.max(1, Number(radiusMeters) || 1),
        color: '#4f46e5',
        fillColor: '#c7d2fe',
        fillOpacity: 0.22,
        weight: 2,
      }).addTo(map)
    } else {
      circleRef.current.setLatLng(center)
      circleRef.current.setRadius(Math.max(1, Number(radiusMeters) || 1))
    }

    if (!markerRef.current) {
      markerRef.current = L.marker(center, {
        draggable: !readOnly,
        title: label,
        icon: pinIcon,
      }).addTo(map)
      markerRef.current.on('dragend', () => {
        const position = markerRef.current?.getLatLng()
        if (!position) return
        callbacksRef.current.onChange({
          latitude: formatCoordinate(position.lat),
          longitude: formatCoordinate(position.lng),
        })
      })
    } else {
      markerRef.current.setLatLng(center)
      if (readOnly) markerRef.current.dragging?.disable()
      else markerRef.current.dragging?.enable()
    }

    markerRef.current.bindTooltip(label, { direction: 'top' })
    map.setView(center, Math.max(map.getZoom(), 17))
  }, [hasPoint, label, latitude, longitude, radiusMeters, readOnly])

  useEffect(() => {
    const map = mapRef.current
    if (!map || hasPoint) return
    if (markerRef.current) {
      markerRef.current.remove()
      markerRef.current = null
    }
    if (circleRef.current) {
      circleRef.current.remove()
      circleRef.current = null
    }
  }, [hasPoint])

  return (
    <div className="setup-leaflet-picker">
      <div ref={containerRef} className="mapper-map setup-leaflet-map" aria-label="Embedded geofence map picker" data-testid="setup-leaflet-map" />
      {!hasPoint ? <p className="helper">Drop the pin by clicking the map after entering an address.</p> : null}
      {mapReady ? <span className="sr-only">Leaflet map ready</span> : null}
    </div>
  )
}
